import React, { useState, useEffect, useRef } from "react";
import { ChevronUp, ChevronDown, Send } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { setToogleHeader } from "../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { sampleQuestions } from "./sampleQuestions";
import QuestionGuide from "./QuestionGuide";
import { getProfileTest } from "../../core/api/auth";

// Function to format markdown bold text (** **) to HTML bold tags
const formatMarkdown = (text) => {
  if (!text) return '';
  // Replace **text** with <strong>text</strong>
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

const SalesDumpAnalysis = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const profile = useSelector((state) => state.profile);
  console.log("Profile in SalesDumpAnalysis:", profile);
  
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [randomQuestions, setRandomQuestions] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [showSqlQuery, setShowSqlQuery] = useState(false);
  const [showQueryResult, setShowQueryResult] = useState(true);
  const [excludeRecentData, setExcludeRecentData] = useState(false);
  const textareaRef = useRef(null);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  
  
  // Function to get 4 random questions with the ANTI-AGING question always first
  const getRandomQuestions = () => {
    // Always include the first question (ANTI-AGING)
    const firstQuestion = sampleQuestions[0];
    
    // Shuffle the remaining questions and take 3
    const remainingQuestions = [...sampleQuestions.slice(1)].sort(() => 0.5 - Math.random()).slice(0, 3);
    
    // Return the ANTI-AGING question plus 3 random questions
    return [firstQuestion, ...remainingQuestions];
  };
  
  // Initialize random questions on component mount
  useEffect(() => {
    setRandomQuestions(getRandomQuestions());
  }, []);
  
  // Auto-resize the textarea whenever question changes
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to get the correct scrollHeight
      textareaRef.current.style.height = 'auto';
      // Set the height based on content
      const newHeight = Math.min(200, Math.max(40, textareaRef.current.scrollHeight));
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [question]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Import the API function from the core API module
      const { askSqlQuestion } = await import('../../core/api/aiAnalysis');
      
      // Call the API endpoint
      const response = await askSqlQuestion({
        question,
        exclude_recent_data: excludeRecentData
      });
      
      // Set the response data
      setResponse(response.data);
    } catch (err) {
      console.error('Error asking SQL question:', err);
      setError(err.response?.data?.error || 'An error occurred while processing your question');
    } finally {
      setLoading(false);
    }
  };

  
    // Permission check
  

  return (
    <div className="">
      <div className="">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Sales Dump Analysis</h4>
            </div>
          </div>

          <ul className="table-top-head">
            <li>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Collapse</Tooltip>}
              >
                <Link
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title">AI Data Analyst</h4>
                  <button 
                    onClick={() => setShowInfo(!showInfo)}
                    className="btn btn-sm btn-primary"
                  >
                    {showInfo ? 'Hide Question Guide' : 'How to Ask Good Questions'}
                  </button>
                </div>
                
                {showInfo && <QuestionGuide />}
                
                <form onSubmit={handleSubmit} className="mb-4">
                  <div className="form-group">
                    <div className="d-flex align-items-center">
                      <div className="position-relative flex-grow-1">
                        <textarea
                          ref={textareaRef}
                          id="question"
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          placeholder="e.g., What are the top 5 campaigns by P/L per buyer?"
                          className="form-control"
                          style={{ minHeight: '40px', maxHeight: '200px', overflowY: 'auto', resize: 'none' }}
                          rows={1}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-sm ml-2"
                        style={{ background: 'none', border: 'none' }}
                        title="Ask Question"
                      >
                        {loading ? (
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                        ) : (
                          <Send size={35} className="text-primary" />
                        )}
                      </button>
                    </div>
                    
                    <div className="form-check mt-2">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        id="excludeRecentData"
                        checked={excludeRecentData}
                        onChange={() => setExcludeRecentData(!excludeRecentData)}
                      />
                      <label className="form-check-label" htmlFor="excludeRecentData">
                        Exclude last 3 months data
                      </label>
                    </div>
                  </div>
                  
                  <div className="mt-3 mb-3">
                    <p className="text-muted mb-2">Try one of these questions:</p>
                    <div className="row">
                      {randomQuestions.map((q, index) => (
                        <div className="col-md-6 mb-2" key={index} style={{ height: '100%' }}>
                          <button
                            type="button"
                            onClick={() => setQuestion(q)}
                            className="btn btn-light w-100 text-left h-100"
                            style={{ 
                              display: 'flex',
                              alignItems: 'flex-start',
                              minHeight: '60px',
                              whiteSpace: 'normal',
                              height: '100%',
                              overflow: 'hidden'
                            }}
                          >
                            {q}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  

                </form>
                
                {error && (
                  <div className="alert alert-danger">
                    <h5 className="font-weight-bold">Error</h5>
                    <p>{error}</p>
                  </div>
                )}
                
                {response && !error && (
                  <div className="card">
                    <div className="card-header">
                      <h5 className="card-title">Answer</h5>
                    </div>
                    <div className="card-body">
                      <p className="card-text" dangerouslySetInnerHTML={{ __html: formatMarkdown(response.answer) }}></p>
                      
                      <div className="mt-4 mb-2">
                        <div 
                          className="d-flex align-items-center" 
                          style={{ cursor: 'pointer' }} 
                          onClick={() => setShowSqlQuery(!showSqlQuery)}
                        >
                          <h5 className="mb-0 mr-2">SQL Query</h5>
                          {showSqlQuery ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        {showSqlQuery && (
                          <pre className="bg-dark text-light p-3 rounded mt-2">
                            {response.query}
                          </pre>
                        )}
                      </div>
                      
                      <div className="mt-4 mb-2">
                        <div 
                          className="d-flex align-items-center" 
                          style={{ cursor: 'pointer' }} 
                          onClick={() => setShowQueryResult(!showQueryResult)}
                        >
                          <h5 className="mb-0 mr-2">Query Result</h5>
                          {showQueryResult ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        {showQueryResult && (
                          <pre className="bg-light p-3 rounded mt-2 text-black" style={{ maxHeight: '240px', overflowX: 'auto' }}>
                            {(() => {
  let parsedData = [];

  try {
    parsedData = JSON.parse(response.result);
  } catch (e) {
    return (
      <div className="alert alert-warning">
        <strong>Warning:</strong> Could not parse query result.
      </div>
    );
  }

  if (!Array.isArray(parsedData) || parsedData.length === 0) {
    return <p className="text-muted">No data available.</p>;
  }

  return (
    <div className="table-responsive mt-2">
      <table className="table table-bordered table-striped table-sm">
        <thead className="thead-dark">
          <tr>
            {Object.keys(parsedData[0]).map((key) => (
              <th key={key}>{key.replace(/_/g, ' ').toUpperCase()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsedData.map((row, index) => (
            <tr key={index}>
              {Object.values(row).map((val, i) => (
                <td key={i}>{typeof val === 'number' ? val.toFixed(2) : val}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
})()}

                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDumpAnalysis;
